import {FormikProps} from "formik";
import {Row, Col, Button} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import _ from "lodash";

import {CallForPapers, FormWidget, PaperSubmission, TaskProps} from "../../types/types";
import I18nText from "../../i18n/I18nText";

// Interface für die TaskContainer Komponente
interface TaskContainerProps {
    formikProps: FormikProps<any>;
    submission?: PaperSubmission;
    callForPapers: CallForPapers;
    isValidForm: boolean;
}

/**
 * Scrollen zu einem direkten HTMLElement element
 * @param element
 */
function scrollTo(element: HTMLElement) {
    // Position des Elements ermitteln mit einem negativen Offset von 40px, damit das Label zu sehen ist
    const y = element.getBoundingClientRect().top + window.pageYOffset - 40;
    window.scrollTo({top: y, behavior: 'smooth'});

    // focus nach einer halben Sekunde setzen, nachdem zu dem Feld gescrollt wurde
    setTimeout(() => {
        element.focus();
    }, 500)
}

/**
 * Rendert eine Box überhalb einer existierenden Einreichung, welche verschiedene "Aufgaben" enthält.
 * Aufgaben sind hierbei sowohl unausgefüllte Pflichtfelder aber auch custom-Aufgaben, welche vom Projektteam im Backend
 * an der Einreichung erstellt werden können.
 *
 * @param formikProps
 * @param formRef
 * @param submission
 * @param isValidForm
 * @constructor
 */
const TaskContainer: (
    {
        formikProps,
        submission,
        callForPapers,
        isValidForm
    }: TaskContainerProps) => JSX.Element | null = (
    {
        formikProps,
        submission,
        callForPapers,
        isValidForm
    }: TaskContainerProps) => {

    const intl = useIntl();
    const [tasks, setTasks] = useState<TaskProps[]>([]);

    useEffect(() => {
        if (submission) {
            setTasks(submission.tasks)
        }
    }, [submission])

    /**
     * Scrollt zu einem Eingabefeld, dessen name-prop dem scrollKey entspricht und fokussiert dieses.
     * Existiert kein Formularfeld für einen scrollKey wird angenommen, dass es sich um das UploadWidget handelt.
     *
     * @param scrollKey
     */
    const scrollToField: (scrollKey: string) => void = (scrollKey: string) => {
        const idElement: HTMLElement | null = document.getElementById(scrollKey);

        if(idElement) {
            scrollTo(idElement)
            return
        }

        let element: NodeListOf<HTMLElement> | HTMLCollectionOf<HTMLElement> = document.getElementsByName(scrollKey);

        // Wenn das Element nicht anhand des Namens gefunden werden kann, handelt es sich um den FileUpload
        if (element.length === 0) {
            element = document.getElementsByClassName('file-upload') as NodeListOf<HTMLElement> | HTMLCollectionOf<HTMLElement>;
        }

        if (element.length > 0) {
            scrollTo(element[0])
        }
    }

    /**
     * Verändert den boolschen Status eines Tasks und streicht den Text durch, wenn der Task inaktiv ist
     *
     * @param task
     */
    const changeTaskStatus: (task: TaskProps) => void = (task: TaskProps) => {
        const elementById = document.getElementById('task-' + task.id);
        if (elementById) {
            if (task.isActive) {
                elementById.classList.add('completedTask');
            } else {
                elementById.classList.remove('completedTask');
            }
            task.isActive = !task.isActive;

            const updatedTasks: TaskProps[] = [...tasks];
            updatedTasks[_.findIndex(updatedTasks, x => x.id === task.id)] = task;
            setTasks(updatedTasks);
        }
    }

    /**
     * Rendert die custom-Tasks, welche von dem Projektteam im Backend an einer Einreichung angelegt werden können.
     * Ein Task besteht immer aus id, dem task aka der Aufgabe (z.B. mehr Bilder hochladen) und dem Zustand isActive.
     */
    const renderSubmissionTasks: () => JSX.Element[] = () => {
        return _.map(tasks, (task: TaskProps) => (
            <Row className='paperSubmissionTask' key={`task-container-${task.id}`}>
                <Col xs={1}>
                    <Button size="sm" variant={task.isActive ? 'success' : 'danger'}
                            onClick={() => changeTaskStatus(task)}>
                        <i className={task.isActive ? 'fa fa-check' : 'fa fa-times'}/>
                    </Button>
                </Col>
                <Col xs={11}>
                    <span id={"task-" + task.id} className={!task.isActive ? 'completedTask' : ''}>
                        {task.task}
                    </span>
                </Col>
            </Row>
        ));
    }

    /**
     * Rendert Buttons mit den i18n-Labels der BASE-Pflichtfelder, also "Titel", "Untertitel", "Abstract" und
     * "Themengebiet", sofern diese unausgeüllt sind. Diese Buttons scrollen onClick zu dem jeweiligen Feld und
     * fokussieren es.
     */
    const renderInvalidFieldTasks: () => JSX.Element | null = () => {
        const invalidFields = _.map(_.pickBy(callForPapers.form.widgets, (widget: FormWidget, key: string) => {
            const value = formikProps.values.widgets[widget.id]
            return widget.baseWidget && _.isEmpty(value);
        }), (value, key) => {
            const widget: FormWidget | undefined = _.find(callForPapers.form.widgets, (widget: FormWidget) => {
                return widget.type === key.toLowerCase() ||
                    widget.type === `${key.toLowerCase()}_input` ||
                    widget.type === `${key.toLowerCase()}_select`;
            });

            if (widget) {
                return (
                    <Row className='paperSubmissionTaskInvalid' key={"invalid-field-task-" + key}>
                        <Col>
                            <Button size='sm' onClick={() => scrollToField(key)}>
                                {widget.label[intl.locale]}
                                <i className='fa fa-hand-pointer-o'/>
                            </Button>
                        </Col>
                    </Row>
                );
            }

            return null;
        })

        if (invalidFields.length > 0) {
            return (
                <>
                    <p>
                        <FormattedMessage id='submission.tasks.required'/>
                        <br/>
                        <span>
                            <FormattedMessage id='submission.tasks.required.hint'/>
                        </span>
                    </p>
                    {invalidFields}
                </>
            );
        }
        return null;
    }

    /**
     * Rendert Buttons mit den i18n-Labels der OPTIONALEN-Pflichtfelder sofern diese unausgeüllt sind.
     * Diese Buttons scrollen onClick zu dem jeweiligen Feld und fokussieren es.
     */
    const renderOptionalInvalidFieldTasks: () => JSX.Element | null = () => {

        const invalidFields = _.map(_.filter(_.filter(callForPapers.form.widgets, {'baseWidget': false, 'deletable': true}), function(widget: FormWidget) {
            const value = formikProps.values.widgets[widget.id] || undefined
            return widget.required &&  _.isEmpty(value) && !_.isNumber(value);
        }), (widget: FormWidget) => {
            const scrollKey = `widgets.${widget.id}`;

            return (
                <Row className='paperSubmissionTaskOptionalInvalid' key={"invalid-field-task-" + widget.id}>
                    <Col>
                        <Button size='sm' onClick={() => scrollToField(scrollKey)}>
                            <I18nText content={widget.label} />
                            <i className='fa fa-hand-pointer-o'/>
                        </Button>
                    </Col>
                </Row>
            );
        });

//
//
//         console.log(widgetsWithRequiredValues)
// const widgetsWithRequiredValues = _.filter(callForPapers.form.widgets, {'baseWidget': false, 'deletable': true})
//         const invalidFields = _.map(_.pickBy(formikProps.values.widgets, (value, key) => {
//             const widget: FormWidget | undefined = _.find(widgetsWithRequiredValues, (widget: FormWidget) => {
//                 return widget.id === key;
//             });
//
//             const isRequired = widget ? widget.required : false;
//
//             // _.isEmpty == true bei Zahlen, daher die extra Abfrage
//             return isRequired && _.isEmpty(value) && !_.isNumber(value);
//         }), (value, key) => {
//             const formDatum: PaperSubmissionFormDatum | undefined = _.find(submission?.formData, (formDatum: PaperSubmissionFormDatum) => {
//                 return formDatum.id === key;
//             });
//
//             const scrollKey = `widgets.${key}${formDatum?.type === 'choice_multiple' ? '-0' : ''}`;
//             if (formDatum) {
//                 return (
//                     <Row className='paperSubmissionTaskOptionalInvalid' key={"invalid-field-task-" + key}>
//                         <Col>
//                             <Button size='sm' onClick={() => scrollToField(scrollKey)}>
//                                 {formDatum.key}
//                                 <i className='fa fa-hand-pointer-o'/>
//                             </Button>
//                         </Col>
//                     </Row>
//                 );
//             }
//
//             return null;
//         })

        if (invalidFields.length > 0) {
            return (
                <>
                    <p>
                        <FormattedMessage id='submission.tasks.optional.required'/>
                        <br/>
                        <span>
                            <FormattedMessage id='submission.tasks.optional.required.hint'/>
                        </span>
                    </p>
                    {invalidFields}
                </>
            );
        }
        return null;
    }

    if (submission?.tasks) {
        const hasTasks = submission.tasks.length > 0;
        if (hasTasks || !isValidForm) {
            return (
                <Row className='paperSubmissionTasksContainer'>
                    {!isValidForm &&
                    <Col sm={4}>
                        {renderInvalidFieldTasks()}
                        {renderOptionalInvalidFieldTasks()}
                    </Col>}

                    {hasTasks &&
                    <Col>
                        <p>
                            <FormattedMessage id='submission.tasks.custom'/>
                        </p>
                        {renderSubmissionTasks()}
                    </Col>
                    }
                    <Col sm={12} className='submitHint'>
                        <span>
                            <FormattedMessage id='submission.tasks.submitHint'/>
                        </span>
                    </Col>
                </Row>
            );
        }
    }

    return null;
}

export default TaskContainer;